import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Trash2, Plus, Minus } from 'lucide-react';
import type { CartItem } from '../types';
import { formatCentsUSD } from '../lib/money';
import { getProductImageSrc } from '../lib/productImages';
import { computeCartTotals, computeSubtotalCentsUSD } from '../lib/pricing';
import { usePricingConfig } from '../hooks/usePricingConfig';
import { OrderingComingSoon } from '../components/OrderingComingSoon';

interface CartPageProps {
  items: CartItem[];
  onUpdateQuantity: (productId: string, quantity: number) => void;
  onRemoveItem: (productId: string) => void;
}

export function CartPage({ items, onUpdateQuantity, onRemoveItem }: CartPageProps) {
  // Pricing always comes from the backend — never hardcoded. null until loaded.
  const pricing = usePricingConfig();

  // Subtotal needs no pricing rules, so show it immediately; shipping/tax/total
  // come from the backend-provided pricing once it loads.
  const subtotalCentsUSD = computeSubtotalCentsUSD(items);
  const totals = pricing ? computeCartTotals(items, pricing) : null;
  const freeShippingThresholdCentsUSD = pricing?.freeShippingThresholdCentsUSD ?? null;

  // Retail-only "Coming Soon" mode — a stale cart (e.g. persisted in
  // localStorage before ordering was turned off) must not reach checkout.
  if (pricing && !pricing.onlineOrderingEnabled) {
    return <OrderingComingSoon />;
  }

  if (items.length === 0) {
    return (
      <div>
        <section className="bg-gray-50 py-12">
          <div className="container-max">
            <h1 className="text-4xl font-bold text-gray-900">Shopping Cart</h1>
          </div>
        </section>
        <section className="py-16">
          <div className="container-max text-center">
            <p className="text-lg text-gray-500 mb-8">Your cart is empty</p>
            <Link to="/products">
              <Button size="lg">Continue Shopping</Button>
            </Link>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div>
      <section className="bg-gray-50 py-12">
        <div className="container-max">
          <h1 className="text-4xl font-bold text-gray-900">Shopping Cart</h1>
        </div>
      </section>

      <section className="py-12">
        <div className="container-max grid md:grid-cols-3 gap-8">
          <div className="md:col-span-2 space-y-4">
            {items.map((item) => (
              <div key={item.product.id} className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 sm:p-6">
                <div className="flex gap-4 items-start">
                  <div className="w-20 h-20 sm:w-24 sm:h-24 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0">
                    <img
                      src={getProductImageSrc(item.product)}
                      alt={item.product.name}
                      className="w-full h-full object-contain p-1"
                    />
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-base sm:text-lg">{item.product.name}</h3>
                    <p className="text-gray-500 text-sm mt-0.5 line-clamp-2 sm:line-clamp-1">{item.product.description}</p>
                    <p className="font-semibold text-coastal-600 mt-1">{formatCentsUSD(item.product.priceCentsUSD)}</p>
                  </div>
                </div>

                <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
                  <div className="flex items-center gap-1.5">
                    <button
                      onClick={() => onUpdateQuantity(item.product.id, Math.max(1, item.quantity - 1))}
                      className="p-1.5 hover:bg-gray-100 rounded-md border border-gray-200"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="w-8 text-center font-semibold">{item.quantity}</span>
                    <button
                      onClick={() => onUpdateQuantity(item.product.id, item.quantity + 1)}
                      className="p-1.5 hover:bg-gray-100 rounded-md border border-gray-200"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="font-bold text-lg">{formatCentsUSD(item.product.priceCentsUSD * item.quantity)}</p>
                    <button
                      onClick={() => onRemoveItem(item.product.id)}
                      className="text-red-500 hover:text-red-700 p-1.5 hover:bg-red-50 rounded-md transition-colors"
                      aria-label={`Remove ${item.product.name}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div>
            <Card className="sticky top-20">
              <CardHeader>
                <CardTitle>Order Summary</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Subtotal</span>
                    <span>{formatCentsUSD(subtotalCentsUSD)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Shipping</span>
                    <span className={totals && totals.shippingCentsUSD === 0 ? 'text-green-600 font-semibold' : 'text-gray-400'}>
                      {totals ? (totals.shippingCentsUSD === 0 ? 'Free' : formatCentsUSD(totals.shippingCentsUSD)) : 'Calculating…'}
                    </span>
                  </div>
                  {totals && totals.shippingCentsUSD === 0 && freeShippingThresholdCentsUSD != null && (
                    <p className="text-xs text-green-600">
                      Free shipping on orders over {formatCentsUSD(freeShippingThresholdCentsUSD)}!
                    </p>
                  )}
                  <div className="flex justify-between">
                    <span className="text-gray-600">Tax</span>
                    <span className={totals ? '' : 'text-gray-400'}>{totals ? formatCentsUSD(totals.taxCentsUSD) : 'Calculating…'}</span>
                  </div>
                  <div className="border-t pt-3 flex justify-between font-bold text-lg">
                    <span>Total</span>
                    <span>{totals ? formatCentsUSD(totals.totalCentsUSD) : '—'}</span>
                  </div>
                </div>
                <Link to="/checkout">
                  <Button className="w-full mb-3" size="lg">Proceed to Checkout</Button>
                </Link>
                <Link to="/products">
                  <Button variant="outline" className="w-full">Continue Shopping</Button>
                </Link>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>
    </div>
  );
}
